import { baseApi } from '@/store/baseApi';
import { CacheDurations } from '@web/utils';

import type {
  CreateJobDeliverablePayload,
  CreateJobDeliverablePoolPayload,
  CreateJobChangeOrderItemPayload,
  CreateJobChangeOrderPayload,
  CreateJobPayload,
  CreateJobResponse,
  DuplicateJobPayload,
  DuplicateJobResponse,
  GetJobChangeOrdersParams,
  GetJobChangeOrdersResponse,
  JobChangeOrder,
  JobChangeOrderDetail,
  JobChangeOrderItem,
  GetJobDetailResponse,
  GetJobDeliverablesParams,
  GetJobDeliverablesResponse,
  JobNetNetResponse,
  JobPerformanceResponse,
  GetJobsParams,
  GetJobsResponse,
  Job,
  JobDeliverable,
  JobDeliverableServiceTypePool,
  JobStatus,
  JobSuggestedNumberResponse,
  UpdateJobDeliverablePayload,
  UpdateJobDeliverableConfidencePayload,
  UpdateJobDeliverablePoolPayload,
  UpdateJobChangeOrderItemPayload,
  UpdateJobChangeOrderPayload,
  UpdateJobPayload,
  UpdateJobResponse,
} from '@/features/jobs/types';
import type { APIResponse } from '@web/types';

type GetJobDeliverablesArgs = {
  jobId: string;
  params?: GetJobDeliverablesParams;
};

type TransitionJobArgs = {
  jobId: string;
  to: JobStatus;
  completed_at?: string | null;
};

type PatchJobArgs = {
  jobId: string;
  payload: UpdateJobPayload;
};

type GetJobPerformanceArgs = {
  jobId: string;
  retainerCycleId?: string | null;
};

type GetJobArgs =
  | string
  | {
      jobId: string;
      retainerCycleId?: string | null;
    };

const getJobIdFromArgs = (args: GetJobArgs) =>
  typeof args === 'string' ? args : args.jobId;

type CreateJobDeliverableArgs = {
  jobId: string;
  payload: CreateJobDeliverablePayload;
};

type PatchJobDeliverableArgs = {
  jobId?: string;
  deliverableId: string;
  payload: UpdateJobDeliverablePayload;
};

type PatchJobDeliverableConfidenceArgs = {
  jobId?: string;
  deliverableId: string;
  payload: UpdateJobDeliverableConfidencePayload;
};

type CreateJobDeliverablePoolArgs = {
  deliverableId: string;
  payload: CreateJobDeliverablePoolPayload;
};

type PatchJobDeliverablePoolArgs = {
  poolId: string;
  payload: UpdateJobDeliverablePoolPayload;
};

type GetJobChangeOrdersArgs = {
  jobId: string;
  params?: GetJobChangeOrdersParams;
};

type CreateJobChangeOrderArgs = {
  jobId: string;
  payload: CreateJobChangeOrderPayload;
};

type PatchJobChangeOrderArgs = {
  changeOrderId: string;
  payload: UpdateJobChangeOrderPayload;
};

type ApplyJobChangeOrderArgs = {
  changeOrderId: string;
  jobId: string;
};

type CreateJobChangeOrderItemArgs = {
  changeOrderId: string;
  payload: CreateJobChangeOrderItemPayload;
};

type PatchJobChangeOrderItemArgs = {
  itemId: string;
  payload: UpdateJobChangeOrderItemPayload;
};

export const jobsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getJobs: builder.query<APIResponse<GetJobsResponse>, GetJobsParams | void>({
      query: (params) => ({
        url: 'jobs',
        method: 'GET',
        params: params ?? undefined,
      }),
      keepUnusedDataFor: CacheDurations.SHORT,
      providesTags: ['Jobs'],
    }),
    getJobDeliverables: builder.query<
      APIResponse<GetJobDeliverablesResponse>,
      GetJobDeliverablesArgs
    >({
      query: ({ jobId, params }) => ({
        url: `jobs/${jobId}/deliverables`,
        method: 'GET',
        params: params ?? undefined,
      }),
      keepUnusedDataFor: CacheDurations.SHORT,
      providesTags: (_result, _error, args) => [
        { type: 'JobDeliverables', id: args.jobId },
      ],
    }),
    transitionJob: builder.mutation<APIResponse<Job>, TransitionJobArgs>({
      query: ({ jobId, to, completed_at }) => ({
        url: `jobs/${jobId}/transition`,
        method: 'POST',
        body: {
          to,
          ...(completed_at !== undefined ? { completed_at } : {}),
        },
      }),
      invalidatesTags: (_result, _error, args) => [
        { type: 'Job', id: args.jobId },
        'Jobs',
      ],
    }),
    createJob: builder.mutation<
      APIResponse<CreateJobResponse>,
      CreateJobPayload
    >({
      query: (payload) => ({
        url: 'jobs',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['Jobs'],
    }),
    getSuggestedJobNumber: builder.query<
      APIResponse<JobSuggestedNumberResponse>,
      void
    >({
      query: () => ({
        url: 'jobs/suggested-number',
        method: 'GET',
      }),
      keepUnusedDataFor: 0,
    }),
    duplicateJob: builder.mutation<
      APIResponse<DuplicateJobResponse>,
      DuplicateJobPayload
    >({
      query: (payload) => ({
        url: 'jobs/duplicate',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['Jobs'],
    }),
    getJob: builder.query<APIResponse<GetJobDetailResponse>, GetJobArgs>({
      query: (args) => {
        const jobId = getJobIdFromArgs(args);
        const retainerCycleId =
          typeof args === 'string' ? null : args.retainerCycleId;

        return {
          url: `jobs/${jobId}`,
          method: 'GET',
          params: retainerCycleId
            ? { retainer_cycle_id: retainerCycleId }
            : undefined,
        };
      },
      keepUnusedDataFor: CacheDurations.SHORT,
      providesTags: (_result, _error, args) => [
        { type: 'Job', id: getJobIdFromArgs(args) },
      ],
    }),
    getJobNetNet: builder.query<APIResponse<JobNetNetResponse>, string>({
      query: (jobId) => ({
        url: `jobs/${jobId}/net-net`,
        method: 'GET',
      }),
      keepUnusedDataFor: CacheDurations.SHORT,
    }),
    getJobPerformance: builder.query<
      APIResponse<JobPerformanceResponse>,
      GetJobPerformanceArgs
    >({
      query: ({ jobId, retainerCycleId }) => ({
        url: `jobs/${jobId}/performance`,
        method: 'GET',
        params: {
          retainer_cycle_id: retainerCycleId ?? undefined,
        },
      }),
      keepUnusedDataFor: CacheDurations.SHORT,
      providesTags: (_result, _error, args) => [
        { type: 'JobPerformance', id: args.jobId },
      ],
    }),
    patchJob: builder.mutation<APIResponse<UpdateJobResponse>, PatchJobArgs>({
      query: ({ jobId, payload }) => ({
        url: `jobs/${jobId}`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (_result, _error, args) => [
        { type: 'Job', id: args.jobId },
        { type: 'JobDeliverables', id: args.jobId },
        'Jobs',
      ],
    }),
    deleteJob: builder.mutation<APIResponse<null>, string>({
      query: (jobId) => ({
        url: `jobs/${jobId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, jobId) => [
        { type: 'Job', id: jobId },
        'Jobs',
      ],
    }),
    createJobDeliverable: builder.mutation<
      APIResponse<JobDeliverable>,
      CreateJobDeliverableArgs
    >({
      query: ({ jobId, payload }) => ({
        url: `jobs/${jobId}/deliverables`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_result, _error, args) => [
        { type: 'JobDeliverables', id: args.jobId },
      ],
    }),
    patchJobDeliverable: builder.mutation<
      APIResponse<JobDeliverable>,
      PatchJobDeliverableArgs
    >({
      query: ({ deliverableId, payload }) => ({
        url: `jobs/deliverables/${deliverableId}`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (result, _error, args) => {
        const jobId = args.jobId ?? result?.data?.job;

        return jobId
          ? [
              { type: 'Job' as const, id: jobId },
              { type: 'JobDeliverables' as const, id: jobId },
            ]
          : [{ type: 'JobDeliverables' as const }];
      },
    }),
    patchJobDeliverableConfidence: builder.mutation<
      APIResponse<JobDeliverable>,
      PatchJobDeliverableConfidenceArgs
    >({
      query: ({ deliverableId, payload }) => ({
        url: `jobs/deliverables/${deliverableId}/confidence`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (result, _error, args) => {
        const jobId = args.jobId ?? result?.data?.job;

        return jobId
          ? [
              { type: 'Job' as const, id: jobId },
              { type: 'JobDeliverables' as const, id: jobId },
            ]
          : [{ type: 'JobDeliverables' as const }];
      },
    }),
    deleteJobDeliverable: builder.mutation<APIResponse<null>, string>({
      query: (deliverableId) => ({
        url: `jobs/deliverables/${deliverableId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'JobDeliverables' }],
    }),
    createJobDeliverablePool: builder.mutation<
      APIResponse<JobDeliverableServiceTypePool>,
      CreateJobDeliverablePoolArgs
    >({
      query: ({ deliverableId, payload }) => ({
        url: `jobs/deliverables/${deliverableId}/pools`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (result) =>
        result ? [{ type: 'JobDeliverables' }] : [],
    }),
    patchJobDeliverablePool: builder.mutation<
      APIResponse<JobDeliverableServiceTypePool>,
      PatchJobDeliverablePoolArgs
    >({
      query: ({ poolId, payload }) => ({
        url: `jobs/deliverables/pools/${poolId}`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (result) =>
        result ? [{ type: 'JobDeliverables' }] : [],
    }),
    deleteJobDeliverablePool: builder.mutation<APIResponse<null>, string>({
      query: (poolId) => ({
        url: `jobs/deliverables/pools/${poolId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'JobDeliverables' }],
    }),
    getJobChangeOrders: builder.query<
      APIResponse<GetJobChangeOrdersResponse>,
      GetJobChangeOrdersArgs
    >({
      query: ({ jobId, params }) => ({
        url: `jobs/${jobId}/change-orders`,
        method: 'GET',
        params: params ?? undefined,
      }),
      keepUnusedDataFor: CacheDurations.SHORT,
      providesTags: (_result, _error, args) => [
        { type: 'JobChangeOrders', id: args.jobId },
      ],
    }),
    createJobChangeOrder: builder.mutation<
      APIResponse<JobChangeOrderDetail>,
      CreateJobChangeOrderArgs
    >({
      query: ({ jobId, payload }) => ({
        url: `jobs/${jobId}/change-orders`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_result, _error, args) => [
        { type: 'Job', id: args.jobId },
        { type: 'JobChangeOrders', id: args.jobId },
        'Jobs',
      ],
    }),
    getJobChangeOrder: builder.query<APIResponse<JobChangeOrderDetail>, string>(
      {
        query: (changeOrderId) => ({
          url: `jobs/change-orders/${changeOrderId}`,
          method: 'GET',
        }),
        keepUnusedDataFor: CacheDurations.SHORT,
        providesTags: (_result, _error, changeOrderId) => [
          { type: 'JobChangeOrder', id: changeOrderId },
        ],
      },
    ),
    patchJobChangeOrder: builder.mutation<
      APIResponse<JobChangeOrderDetail>,
      PatchJobChangeOrderArgs
    >({
      query: ({ changeOrderId, payload }) => ({
        url: `jobs/change-orders/${changeOrderId}`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (_result, _error, args) => [
        { type: 'JobChangeOrder', id: args.changeOrderId },
        { type: 'JobChangeOrders' },
      ],
    }),
    deleteJobChangeOrder: builder.mutation<APIResponse<null>, string>({
      query: (changeOrderId) => ({
        url: `jobs/change-orders/${changeOrderId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, changeOrderId) => [
        { type: 'JobChangeOrder', id: changeOrderId },
        { type: 'JobChangeOrders' },
        { type: 'JobDeliverables' },
        { type: 'JobPerformance' },
        'Jobs',
      ],
    }),
    applyJobChangeOrder: builder.mutation<
      APIResponse<JobChangeOrder>,
      ApplyJobChangeOrderArgs
    >({
      query: ({ changeOrderId }) => ({
        url: `jobs/change-orders/${changeOrderId}/apply`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { changeOrderId, jobId }) => [
        { type: 'JobChangeOrder', id: changeOrderId },
        { type: 'Job', id: jobId },
        { type: 'JobChangeOrders' },
        { type: 'JobDeliverables' },
        { type: 'JobPerformance' },
        'Jobs',
      ],
    }),
    createJobChangeOrderItem: builder.mutation<
      APIResponse<JobChangeOrderItem>,
      CreateJobChangeOrderItemArgs
    >({
      query: ({ changeOrderId, payload }) => ({
        url: `jobs/change-orders/${changeOrderId}/items`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_result, _error, args) => [
        { type: 'JobChangeOrder', id: args.changeOrderId },
      ],
    }),
    patchJobChangeOrderItem: builder.mutation<
      APIResponse<JobChangeOrderItem>,
      PatchJobChangeOrderItemArgs
    >({
      query: ({ itemId, payload }) => ({
        url: `jobs/change-orders/items/${itemId}`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: [{ type: 'JobChangeOrder' }],
    }),
    deleteJobChangeOrderItem: builder.mutation<APIResponse<null>, string>({
      query: (itemId) => ({
        url: `jobs/change-orders/items/${itemId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'JobChangeOrder' }],
    }),
  }),
});

export const {
  useGetJobsQuery,
  useLazyGetJobsQuery,
  useGetJobDeliverablesQuery,
  useLazyGetJobDeliverablesQuery,
  useTransitionJobMutation,
  useCreateJobMutation,
  useGetSuggestedJobNumberQuery,
  useDuplicateJobMutation,
  useGetJobQuery,
  useGetJobNetNetQuery,
  useGetJobPerformanceQuery,
  usePatchJobMutation,
  useDeleteJobMutation,
  useCreateJobDeliverableMutation,
  usePatchJobDeliverableMutation,
  usePatchJobDeliverableConfidenceMutation,
  useDeleteJobDeliverableMutation,
  useCreateJobDeliverablePoolMutation,
  usePatchJobDeliverablePoolMutation,
  useDeleteJobDeliverablePoolMutation,
  useGetJobChangeOrdersQuery,
  useCreateJobChangeOrderMutation,
  useGetJobChangeOrderQuery,
  usePatchJobChangeOrderMutation,
  useDeleteJobChangeOrderMutation,
  useApplyJobChangeOrderMutation,
  useCreateJobChangeOrderItemMutation,
  usePatchJobChangeOrderItemMutation,
  useDeleteJobChangeOrderItemMutation,
} = jobsApi;
